import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Phone, Mail, X } from 'lucide-react'
import { BrandLogo, Button, Input } from '@components/ui'
import api from '@services/api'
import { useAuthStore, UserRole } from '@store/useAuthStore'

// Mapear functionId para role
const functionIdToRole = (functionId?: number): UserRole | null => {
  if (!functionId) return null
  // functionId 1 = receptionist, 2 = mechanic, 3+ = administrator
  if (functionId === 1) return 'receptionist'
  if (functionId === 2) return 'mechanic'
  return 'administrator'
}

interface LoginResponse {
  token: string
  hasApplicationAccess?: boolean
  user: {
    id: number
    name: string
    email: string
    functionId?: number
    workshopCompanyId?: number
    active?: boolean
    /** Perfil alinhado com Spring (ROLE_*); preferir a inferência por functionId. */
    role?: UserRole
  }
}

export function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuthStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showCurl, setShowCurl] = useState(false)
  const [showAccountRequestModal, setShowAccountRequestModal] = useState(false)
  const [accountRequest, setAccountRequest] = useState({ name: '', email: '', message: '' })
  const [accountRequestLoading, setAccountRequestLoading] = useState(false)
  const [accountRequestError, setAccountRequestError] = useState<string | null>(null)
  const [accountRequestSuccess, setAccountRequestSuccess] = useState<string | null>(null)
  const [accountRequestSuccessFading, setAccountRequestSuccessFading] = useState(false)

  const handleAccountRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setAccountRequestError(null)
    setAccountRequestSuccess(null)
    setAccountRequestSuccessFading(false)
    setAccountRequestLoading(true)
    try {
      await api.post('/public/account-request', {
        name: accountRequest.name,
        email: accountRequest.email,
        message: accountRequest.message || '',
      })
      setAccountRequestSuccess('A equipa recebeu o seu email e aguarda pela sua resposta')
      setAccountRequest({ name: '', email: '', message: '' })
      // 5s visível, depois fade out 1.5s
      setTimeout(() => {
        setAccountRequestSuccessFading(true)
        setTimeout(() => {
          setAccountRequestSuccess(null)
          setAccountRequestSuccessFading(false)
        }, 1500)
      }, 5000)
    } catch {
      setAccountRequestError('Ocorreu um erro no envio de pedido. Entre em contacto connosco através do email ou número de telemóvel')
    } finally {
      setAccountRequestLoading(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      const response = await api.post('/auth/login', { email, password })
      const payload: LoginResponse = response.data?.data ?? response.data

      if (!payload?.token || !payload?.user) {
        throw new Error('Resposta inesperada do servidor')
      }

      const VALID_ROLES: readonly UserRole[] = ['receptionist', 'mechanic', 'administrator']
      const fromApi = payload.user.role
      const role: UserRole | null =
        fromApi && VALID_ROLES.includes(fromApi) ? fromApi : functionIdToRole(payload.user.functionId)
      if (!role) {
        throw new Error('Função do utilizador não reconhecida')
      }

      const hasApplicationAccess = payload.hasApplicationAccess !== false
      login(payload.user, payload.token, role, hasApplicationAccess)

      if (!hasApplicationAccess) {
        navigate('/subscription-required', { replace: true })
        return
      }

      // Após login: `/` é a landing; com sessão o RootPage redireciona para `/homepage` (app).
      navigate('/', { replace: true })
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Não foi possível iniciar sessão. Verifique as credenciais.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-[#17253C] flex items-center justify-center px-4">
      <div className="w-full max-w-md space-y-8 rounded-3xl bg-[#17253C] p-8 shadow-2xl border border-white/10">
        <div className="text-center space-y-2">
          <BrandLogo className="mx-auto h-16 max-h-16" />
          <Link
            to="/"
            className="inline-block text-sm text-sky-300/90 hover:text-sky-200 transition-colors"
          >
            ← Voltar à apresentação
          </Link>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label className="text-sm font-medium text-white/80">Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="exemplo@fixdauto.pt"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-white/80">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="input pr-11"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-2 my-auto inline-flex h-9 w-9 items-center justify-center rounded-lg text-white/70 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-300/60"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Ocultar password' : 'Mostrar password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setShowAccountRequestModal(true)}
              className="inline-block text-sm text-gray-400 underline transition-transform duration-200 hover:scale-125 hover:text-gray-300"
            >
              Não tem conta?
            </button>
          </div>

          {error && (
            <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
              {error}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'A entrar...' : 'Entrar'}
          </Button>

          {import.meta.env.DEV && (
            <div className="mt-4 space-y-2">
              <button
                type="button"
                onClick={() => setShowCurl(!showCurl)}
                className="text-xs text-white/60 hover:text-white/80 underline"
              >
                {showCurl ? 'Ocultar' : 'Mostrar'} comando curl para debug
              </button>
              
            </div>
          )}
        </form>
      </div>

      {/* Modal "Não tem conta?" */}
      {showAccountRequestModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={() => setShowAccountRequestModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Pedido de conta"
        >
          <div
            className="relative w-full max-w-md rounded-2xl border border-white/20 bg-[#1e3a5f] p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowAccountRequestModal(false)}
              className="absolute right-4 top-4 text-white/60 transition-colors hover:text-white"
              aria-label="Fechar"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="space-y-6 pr-8">
              <h3 className="text-lg font-semibold text-white/90">| Contactos</h3>
              <div className="flex flex-col gap-3 text-white/90">
                <a
                  href="tel:928490194"
                  className="flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Phone className="h-4 w-4 shrink-0" />
                  <span>928490194</span>
                </a>
                <a
                  href="mailto:equipa.fixdauto@gmail.com"
                  className="flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Mail className="h-4 w-4 shrink-0" />
                  <span>equipa.fixdauto@gmail.com</span>
                </a>
              </div>

              <h3 className="text-lg font-semibold text-white/90">| Formulário</h3>
              <form onSubmit={handleAccountRequestSubmit} className="flex flex-col gap-3">
                {accountRequestError && (
                  <div className="rounded-xl border border-red-300 bg-red-900/30 px-4 py-3 text-sm text-red-200">
                    {accountRequestError}
                  </div>
                )}
                {accountRequestSuccess && (
                  <div
                    className={`rounded-xl border border-green-300 bg-green-900/30 px-4 py-3 text-sm text-green-200 transition-opacity duration-[1500ms] ${
                      accountRequestSuccessFading ? 'opacity-0' : 'opacity-100'
                    }`}
                  >
                    {accountRequestSuccess}
                  </div>
                )}
                <input
                  type="text"
                  placeholder="Nome"
                  value={accountRequest.name}
                  onChange={(e) => setAccountRequest((p) => ({ ...p, name: e.target.value }))}
                  className="w-full rounded-xl border border-white/20 bg-white/5 px-4 py-2.5 text-white placeholder:text-white/40 focus:border-white/40 focus:outline-none focus:ring-1 focus:ring-white/40"
                  required
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={accountRequest.email}
                  onChange={(e) => setAccountRequest((p) => ({ ...p, email: e.target.value }))}
                  className="w-full rounded-xl border border-white/20 bg-white/5 px-4 py-2.5 text-white placeholder:text-white/40 focus:border-white/40 focus:outline-none focus:ring-1 focus:ring-white/40"
                  required
                />
                <textarea
                  placeholder="Pedido de conta"
                  value={accountRequest.message}
                  onChange={(e) => setAccountRequest((p) => ({ ...p, message: e.target.value }))}
                  rows={4}
                  className="w-full resize-none rounded-xl border border-white/20 bg-white/5 px-4 py-2.5 text-white placeholder:text-white/40 focus:border-white/40 focus:outline-none focus:ring-1 focus:ring-white/40"
                />
                <Button type="submit" className="w-full" disabled={accountRequestLoading}>
                  {accountRequestLoading ? 'A enviar...' : 'Enviar pedido'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

